/**
 * Discovery + paint for the scroll affordance (edge fades over the browser's own scrollbar).
 *
 * The scrollbar itself is the browser's: a static stylesheet rule tints it, with no script
 * involvement at all. This module only finds the scrollers and paints the edge fades the geometry
 * seam (`scroll-affordance-geometry.ts`, entered through `scrollAffordanceState`) describes.
 *
 * Everything that differs between the two axes is held in one `ScrollAxis` object
 * (`scroll-affordance-axis.ts`), so the readers and the painter here are each written once and
 * handed the axis they are working on.
 */

import { axisGeometry, FADE_EDGE_NAMES, horizontalAxis, SCROLL_AXES, verticalAxis, type ScrollAxis } from "./scroll-affordance-axis";
import {
   EDGE_EPSILON,
   fadeBoxOf,
   intersectionOf,
   isEmpty,
   scrollAffordanceState,
   type Bounds,
} from "./scroll-affordance-geometry";

// -- Discovery ------------------------------------------------------------------------------------

/**
 * ⚠ This list is duplicated, on purpose, in `src/Tailwind/Components/scroll-affordance.css`, which
 * puts `scrollbar-width`, `scrollbar-gutter` and `scrollbar-color` on exactly the same selector.
 * **Adding or removing an overflow variant here means editing that rule too, and the other way
 * round.**
 *
 * It is not deduplicated because there is nowhere to share it from: the stylesheet is compiled by
 * Tailwind and the script by the TypeScript bundler, neither reads the other, and no build step joins
 * them. Emitting the CSS from this constant, or this constant from the CSS, would buy one source at
 * the price of a generator in the middle of the shared bundle. So both sides name each other instead
 * — the CSS rule carries the same warning pointing back here.
 */
const CANDIDATE_SELECTOR = [
   ".overflow-auto",
   ".overflow-scroll",
   ".overflow-y-auto",
   ".overflow-y-scroll",
   ".overflow-x-auto",
   ".overflow-x-scroll",
   // Variant-prefixed forms (`@3xl:overflow-y-auto`); `isScrollable()` rejects them where the variant is off.
   '[class*=":overflow-auto"]',
   '[class*=":overflow-scroll"]',
   '[class*=":overflow-y-auto"]',
   '[class*=":overflow-y-scroll"]',
   '[class*=":overflow-x-auto"]',
   '[class*=":overflow-x-scroll"]',
   "[data-scrollable]",
].join(", ");

/** Does this element really scroll on this axis, whatever its class says? */
function scrollsOnAxis(axis: ScrollAxis, element: HTMLElement, style: CSSStyleDeclaration): boolean {
   const overflow = axis.overflowOf(style);
   if (overflow !== "auto" && overflow !== "scroll") {
      return false;
   }
   // Same slack the geometry seam uses, so discovery never adopts a scroller it would draw nothing for.
   return axis.totalLengthOf(element) - axis.visibleLengthOf(element) > EDGE_EPSILON;
}

/**
 * `Base/reset.css` unsets nested overflow (`.overflow-auto .overflow-auto { overflow: unset }` and
 * its `-x`/`-y` siblings), so a candidate's own class can lie about whether it actually scrolls.
 * Confirm against computed overflow and real scroll size instead of trusting the class.
 *
 * Which axis scrolls is deliberately not reported: an entry is created for both axes either way, and
 * the geometry seam re-decides each axis's fades on every frame from live measurements, so an answer
 * captured at discovery time would be a second, staler source of truth for the same thing.
 */
function isScrollable(element: HTMLElement): boolean {
   if (element.dataset.scrollable === "off") {
      return false;
   }

   const style = window.getComputedStyle(element);
   return SCROLL_AXES.some((axis) => scrollsOnAxis(axis, element, style));
}

function findScrollers(): HTMLElement[] {
   const found: HTMLElement[] = [];
   for (const element of Array.from(document.body.querySelectorAll<HTMLElement>(CANDIDATE_SELECTOR))) {
      if (element.closest(".scroll-affordance-layer")) {
         continue;
      }
      if (isScrollable(element)) {
         found.push(element);
      }
   }
   return found;
}

// -- Per-scroller state -----------------------------------------------------------------------

/** The overlay elements one axis owns: its two edge fades. */
type AxisOverlay = {
   fadeAtStart: HTMLElement;
   fadeAtEnd: HTMLElement;
};

type ScrollerEntry = {
   element: HTMLElement;
   /**
    * One `overflow: hidden` host per scroller, sized to the part of the scroller that is actually on
    * screen. Everything this scroller paints lives inside it, so a fade is clipped to the scroller's
    * visible intersection instead of floating loose over whatever sits above it (Spec story 20), and
    * a scroller scrolled fully out of view draws nothing at all (story 21).
    */
   clipHost: HTMLElement;
   vertical: AxisOverlay;
   horizontal: AxisOverlay;
   resizeObserver: ResizeObserver;
};

let layer: HTMLElement | null = null;
const entries = new Map<HTMLElement, ScrollerEntry>();
let mutationObserver: MutationObserver | null = null;
let rescanDebounce = 0;
let frameQueued = false;

/** What the rediscovery observer watches. `withoutObserving()` re-attaches with these too. */
const RESCAN_OBSERVER_OPTIONS: MutationObserverInit = { childList: true, subtree: true };

function ensureLayer(): HTMLElement {
   if (!layer || !layer.isConnected) {
      layer = document.createElement("div");
      layer.className = "scroll-affordance-layer";
      layer.setAttribute("aria-hidden", "true");
      document.body.appendChild(layer);
   }
   return layer;
}

/** Attaches a clip host to the layer, creating the layer first if it is missing or detached. */
function attachClipHost(clipHost: HTMLElement): void {
   ensureLayer().appendChild(clipHost);
}

function createOverlayElement(className: string): HTMLElement {
   const el = document.createElement("div");
   el.className = className;
   el.setAttribute("aria-hidden", "true");
   return el;
}

function createAxisOverlay(axis: ScrollAxis): AxisOverlay {
   return {
      fadeAtStart: createOverlayElement(`scroll-affordance-fade ${axis.fadeEdges.fadeAtStart.className}`),
      fadeAtEnd: createOverlayElement(`scroll-affordance-fade ${axis.fadeEdges.fadeAtEnd.className}`),
   };
}

function createEntry(element: HTMLElement): ScrollerEntry {
   const clipHost = createOverlayElement("scroll-affordance-clip");
   attachClipHost(clipHost);

   const vertical = createAxisOverlay(verticalAxis);
   const horizontal = createAxisOverlay(horizontalAxis);

   clipHost.append(vertical.fadeAtStart, vertical.fadeAtEnd, horizontal.fadeAtStart, horizontal.fadeAtEnd);

   const resizeObserver = new ResizeObserver(() => schedulePaint());
   resizeObserver.observe(element);

   element.addEventListener("scroll", schedulePaint, { passive: true });

   return { element, clipHost, vertical, horizontal, resizeObserver };
}

function destroyEntry(entry: ScrollerEntry): void {
   entry.resizeObserver.disconnect();
   entry.element.removeEventListener("scroll", schedulePaint);
   entry.clipHost.remove();
}

// -- Colour --------------------------------------------------------------------------------------

/**
 * The fade colour is not a custom property: it is read at paint time from the scroller's own
 * effective background, walking up to the first ancestor that paints one. White on the paper
 * sheets, dock ink on the dock, with no per-surface configuration.
 */
function effectiveBackground(element: HTMLElement): string {
   let node: HTMLElement | null = element;
   while (node) {
      const color = window.getComputedStyle(node).backgroundColor;
      if (color && color !== "transparent" && !color.startsWith("rgba(0, 0, 0, 0)")) {
         return color;
      }
      node = node.parentElement;
   }
   return "#ffffff";
}

function transparentVersionOf(color: string): string {
   const parts = color.match(/[\d.]+/g);
   return parts && parts.length >= 3 ? `rgba(${parts[0]}, ${parts[1]}, ${parts[2]}, 0)` : "transparent";
}

/**
 * The two ends of a fade's gradient. They travel together because they are one colour read twice —
 * `clear` is always `solid`'s own fully transparent form, never an unrelated value, or the fade would
 * dissolve towards a colour the surface does not have.
 */
type FadeColors = {
   solid: string;
   clear: string;
};

function fadeColorsOf(element: HTMLElement): FadeColors {
   const solid = effectiveBackground(element);
   return { solid, clear: transparentVersionOf(solid) };
}

// -- Where the scroller actually is on screen ---------------------------------------------------

/**
 * The box of every open `<dialog>` on the page.
 *
 * A fade must never wash across an open dialog's edge — the Spec calls that a defect. Stacking order
 * alone does not guarantee it. A non-modal popup at the `z-50` tier only outranks the affordance
 * layer while nothing traps it, and `position: sticky` creates a stacking context on its own, so a
 * `searchable-select` popup opened inside a `DynamicTableRow`'s sticky first column paints inside
 * that column's context — underneath the layer. Verified live on Compliance's People register with
 * two `z-index: 50` probes: the one inside the sticky cell was washed by the bottom fade, the one
 * appended to `<body>` was not.
 */
function openDialogBounds(): Bounds[] {
   return Array.from(document.querySelectorAll<HTMLDialogElement>("dialog[open]")).map((dialog) => dialog.getBoundingClientRect());
}

function overlapsAny(bounds: Bounds, others: readonly Bounds[]): boolean {
   return others.some((other) => !isEmpty(intersectionOf(bounds, other)));
}

/**
 * The viewport, intersected with every ancestor scroll box that clips the scroller.
 *
 * The overlay is drawn in viewport coordinates, so without this a panel scrolled half out of the
 * top of its own scrolling main column would keep painting its fade over the header above it —
 * Spec story 20 asks for the opposite: the panel's fade travels with the panel and never floats
 * loose over the page. An ancestor clips its content whenever either computed overflow axis is
 * anything but `visible`, which covers the `overflow-hidden` wrapper the fill-remaining page kind
 * uses as well as a real scroller.
 */
function visibleBoundsOf(element: HTMLElement): Bounds {
   let bounds: Bounds = { left: 0, top: 0, right: window.innerWidth, bottom: window.innerHeight };

   // A fixed-position scroller's containing block is the viewport, so no ancestor's overflow clips
   // it. Nothing in this suite scrolls from a fixed box today; this keeps the walk honest if one
   // ever does.
   if (window.getComputedStyle(element).position === "fixed") {
      return intersectionOf(bounds, element.getBoundingClientRect());
   }

   for (let ancestor = element.parentElement; ancestor; ancestor = ancestor.parentElement) {
      const style = window.getComputedStyle(ancestor);
      if (style.overflowX === "visible" && style.overflowY === "visible") {
         continue;
      }
      bounds = intersectionOf(bounds, ancestor.getBoundingClientRect());
      if (isEmpty(bounds)) {
         break;
      }
   }

   return intersectionOf(bounds, element.getBoundingClientRect());
}

// -- Painting ------------------------------------------------------------------------------------

/** The top-left corner the coordinates an overlay element is positioned in are measured from. */
type Origin = {
   left: number;
   top: number;
};

/** A clip host is positioned in viewport coordinates, so its own origin is the viewport's corner. */
const VIEWPORT_ORIGIN: Origin = { left: 0, top: 0 };

/** Positions one overlay element, in coordinates relative to the clip host it is drawn inside. */
function positionWithin(element: HTMLElement, bounds: Bounds, origin: Origin): void {
   element.style.left = `${bounds.left - origin.left}px`;
   element.style.top = `${bounds.top - origin.top}px`;
   element.style.width = `${bounds.right - bounds.left}px`;
   element.style.height = `${bounds.bottom - bounds.top}px`;
}

function paintFade(fade: HTMLElement, shouldPaint: boolean, bounds: Bounds, host: Bounds, gradient: string, colors: FadeColors): void {
   if (!shouldPaint) {
      fade.style.display = "none";
      return;
   }

   fade.style.display = "";
   positionWithin(fade, bounds, host);
   fade.style.background = `linear-gradient(${gradient}, ${colors.solid}, ${colors.clear})`;
}

function paintEntry(entry: ScrollerEntry, openDialogs: readonly Bounds[]): void {
   const host = visibleBoundsOf(entry.element);

   // Scrolled fully out of view behind an ancestor's scroll box: draw nothing at all.
   if (isEmpty(host)) {
      entry.clipHost.style.display = "none";
      return;
   }

   entry.clipHost.style.display = "";
   positionWithin(entry.clipHost, host, VIEWPORT_ORIGIN);

   const state = scrollAffordanceState({
      vertical: axisGeometry(verticalAxis, entry.element),
      horizontal: axisGeometry(horizontalAxis, entry.element),
   });

   const colors = fadeColorsOf(entry.element);

   // Fades are drawn inside the panel's client box, not its border box: the client box is the one
   // box that excludes both the panel's borders and whatever gutter the platform's own scrollbar
   // takes, so a fade stops exactly where the scrollbar begins and never washes across it. Measured
   // live every paint — where the platform draws an overlay scrollbar it takes no width, and the
   // client box is simply the panel inset by its borders. `fadeBoxOf` does the arithmetic; this reads
   // the five numbers it needs.
   const fadeBox = fadeBoxOf({
      borderBox: entry.element.getBoundingClientRect(),
      clientOffsetLeft: entry.element.clientLeft,
      clientOffsetTop: entry.element.clientTop,
      clientWidth: entry.element.clientWidth,
      clientHeight: entry.element.clientHeight,
   });

   for (const axis of SCROLL_AXES) {
      const affordance = state[axis.name];
      const overlay = entry[axis.name];

      // One name reaches all three parts of an edge: the seam's decision, the element, and the edge's
      // own bounds and gradient.
      for (const edgeName of FADE_EDGE_NAMES) {
         const edge = axis.fadeEdges[edgeName];
         const bounds = edge.boundsOf(fadeBox);
         const shouldPaint = affordance[edgeName] && !overlapsAny(bounds, openDialogs);

         paintFade(overlay[edgeName], shouldPaint, bounds, host, edge.gradient, colors);
      }
   }
}

function withoutObserving(work: () => void): void {
   mutationObserver?.disconnect();
   work();
   // Drop the records the work produced, so the observer never feeds itself its own DOM writes and
   // loops forever.
   mutationObserver?.takeRecords();
   mutationObserver?.observe(document.body, RESCAN_OBSERVER_OPTIONS);
}

function paint(): void {
   withoutObserving(() => {
      const openDialogs = openDialogBounds();
      for (const entry of entries.values()) {
         paintEntry(entry, openDialogs);
      }
   });
}

function schedulePaint(): void {
   if (frameQueued) {
      return;
   }
   frameQueued = true;
   window.requestAnimationFrame(() => {
      frameQueued = false;
      paint();
   });
}

// -- Rediscovery -----------------------------------------------------------------------------

function rescan(): void {
   const found = new Set(findScrollers());

   // Entry creation and destruction move clip hosts under `document.body`, which the body
   // `MutationObserver` also watches.
   withoutObserving(() => {
      for (const [element, entry] of entries) {
         if (!found.has(element)) {
            destroyEntry(entry);
            entries.delete(element);
         }
      }

      for (const element of found) {
         if (!entries.has(element)) {
            entries.set(element, createEntry(element));
         }
      }

      // A morph can strip the layer while the scroller element keeps its identity, leaving a stale
      // entry with a detached clip host. Re-attach it in place: the clip host still holds its four
      // fade children.
      for (const entry of entries.values()) {
         if (!entry.clipHost.isConnected) {
            attachClipHost(entry.clipHost);
         }
      }
   });

   schedulePaint();
}

/** Window a burst of resize or mutation events is coalesced into, before one rediscovery pass runs. */
export const RESCAN_DEBOUNCE_MS = 150;

function scheduleRescan(): void {
   window.clearTimeout(rescanDebounce);
   rescanDebounce = window.setTimeout(rescan, RESCAN_DEBOUNCE_MS);
}

// -- Wiring ------------------------------------------------------------------------------------

/**
 * Undo steps for everything the install wired, newest first. Every attachment records its own
 * removal in the same statement, so the two cannot drift apart. Non-empty exactly while installed,
 * which is also the install-once guard.
 */
const teardown: Array<() => void> = [];

function listen(target: EventTarget, type: string, handler: EventListener, options?: boolean | AddEventListenerOptions): void {
   target.addEventListener(type, handler, options);
   teardown.push(() => target.removeEventListener(type, handler, options));
}

/**
 * Installs suite-wide scroll-affordance discovery and painting, and returns the uninstall — as
 * `installFocusRestorePolicy()` does. Idempotent: a second call is a no-op and hands back the same
 * uninstall. The shared entry never uninstalls; tests do.
 */
export function installScrollAffordance(): () => void {
   if (teardown.length > 0) {
      return uninstall;
   }

   rescan();
   teardown.push(() => {
      for (const entry of entries.values()) {
         destroyEntry(entry);
      }
      entries.clear();
      layer?.remove();
      layer = null;
   });

   listen(document, "turbo:load", rescan);

   // Scroll does not bubble; a capture-phase document listener catches every scroller (including an
   // ancestor scrolling one of its descendants, e.g. a pick list inside a scrolling main column) in
   // one place.
   listen(document, "scroll", schedulePaint, true);
   // A resize can make a panel start or stop scrolling, not only move one that already does, so it
   // needs both handlers: the repaint keeps the fades of existing entries on their panels through
   // the whole drag, while the debounced rescan picks up which panels scroll now.
   listen(window, "resize", schedulePaint);
   listen(window, "resize", scheduleRescan);
   teardown.push(() => window.clearTimeout(rescanDebounce));

   mutationObserver = new MutationObserver(scheduleRescan);
   mutationObserver.observe(document.body, RESCAN_OBSERVER_OPTIONS);
   teardown.push(() => {
      mutationObserver?.disconnect();
      mutationObserver = null;
   });

   // Opening or closing a dialog is an `open` attribute change, which the child-list-only
   // rediscovery observer above never sees — and a fade has to step aside for an open dialog in the
   // same frame it opens, not whenever something else next happens to schedule a paint. A second
   // observer narrowed to that one attribute costs nothing (it fires only when a dialog opens or
   // closes) and cannot see the paint's own writes, which are inline styles.
   const dialogObserver = new MutationObserver(schedulePaint);
   dialogObserver.observe(document.body, { attributes: true, attributeFilter: ["open"], subtree: true });
   teardown.push(() => dialogObserver.disconnect());

   return uninstall;
}

/** Runs every recorded undo step, newest first. Idempotent — an uninstalled module has none left. */
function uninstall(): void {
   for (const undo of teardown.splice(0).reverse()) {
      undo();
   }
}
